import React from 'react';
import { styles } from './ProgressTrackerSettings.styles';
import { Button, Typography, Box, RadioGroup, FormControlLabel, Radio, Skeleton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { StatusColorModal } from './StatusColorModal';
import { useProgressTrackerSettingsContext } from './context/useProgressTrackerSettingsContext';
import { ProgressTrackerSettingItem } from './ProgressTrackerSettingItem';
import { ProgressTrackerPreview } from './ProgressTrackerPreview';
import { TimelineChartOrientation } from '@opengov/stories-shared/types';
import { IStoriesSettingsMessages } from '../../../../types';
import InfoIcon from '@mui/icons-material/Info';
type ProgressTrackerSettingsProps = {
  messages: IStoriesSettingsMessages;
};

const LoadingSkeleton = () => {
  return (<Box sx={styles.progressTrackerSettingsListContainer}>
    {Array.from({ length: 5 }, (_, index) => (
      <Box key={index} sx={styles.progressTrackerSettingItem}>
        <Box sx={styles.statusColorContainer}>
          <Skeleton variant="rectangular" height={32} width={32} />
          <Skeleton variant="rectangular" height={32} width={140} />
        </Box>
        <Box sx={styles.labelPositioningContainer}>
          <Box sx={styles.labelPositioningToggleButtonGroupContainer}>
            <Skeleton variant="rectangular" height={32} width={120} />
          </Box>
        </Box>
      </Box>))
    }
  </Box >)
}

export const ProgressTrackerSettings: React.FC<ProgressTrackerSettingsProps> = ({ messages }) => {
  const {
    localProgressTrackerSettings: LocalProgressTrackerSettings,
    updateLocalProgressTrackerSettings,
    resetLocalProgressTrackerSettings,
    saveProgressTrackerSettings,
    setShowColorModal,
    activeSettingId,
    setActiveSettingId,
    showColorModal,
    currentAlignment,
    setCurrentAlignment,
    addNewStatus,
    hasChanges,
    isLoading,
    isDataUpdating
  } = useProgressTrackerSettingsContext();

  const handleColorSave = (hex: string) => {
    updateLocalProgressTrackerSettings({
      ...LocalProgressTrackerSettings.find((setting) => setting.id === activeSettingId),
      colorCode: hex
    });
    setShowColorModal(false);
    setActiveSettingId('');
  };
  const handleSave = () => {
    saveProgressTrackerSettings();
  };
  const handleCancel = () => {
    resetLocalProgressTrackerSettings();
  };
  const handleAddStatus = () => {
    addNewStatus();
  };
  const addDisabled = () => {
    let disabled = false;
    const EmptySetting = LocalProgressTrackerSettings.find(
      (setting) => setting.status === '' || setting.colorCode === ''
    );
    const LastSetting = LocalProgressTrackerSettings.length === 7;
    if (EmptySetting || LastSetting) {
      disabled = true;
    }
    return disabled;
  };
  return (
    <>

      <Box sx={styles.progressTrackerSettingsContainer}>
        <Box sx={styles.progressTrackerSettingsHeaderContainer}>
          <Typography sx={styles.progressTrackerSettingsDescription}>{messages.progressTrackerSettingsDescription}</Typography>
          <Box sx={styles.progressTrackerSettingsButtonsContainer}>
            {isDataUpdating ? <Typography sx={styles.progressTrackerSettingsDescription}>{messages.saving}</Typography> : null}
            {hasChanges && (
              <Button sx={styles.button} color="secondary" onClick={handleCancel}>
                {messages.cancel}
              </Button>
            )}
            <Button sx={styles.button} variant="outlined" color="primary" onClick={handleSave} disabled={!hasChanges}>
              {messages.save}
            </Button>
          </Box>
        </Box>
        <Box sx={styles.progressTrackerSettingsContentContainer}>
          <Box sx={styles.progressTrackerSettingsItemContainer}>
            <Box sx={styles.progressTrackerSettingsItemHeaderContainer}>
              <Typography sx={styles.rowHeader} >
                {messages.statusAndColor}
              </Typography>
              <Typography sx={styles.rowHeaderLabelPositioning}>
                {messages.labelPositioning}
                <Tooltip title={messages.labelPositioningTooltip} placement='top'>
                  <InfoIcon aria-hidden={true} />
                </Tooltip>
              </Typography>
            </Box>
            {

              isLoading ? <LoadingSkeleton /> :
                <Box sx={styles.progressTrackerSettingsListContainer}>
                  {LocalProgressTrackerSettings.map((setting) => (
                    <ProgressTrackerSettingItem key={setting.id} {...setting} />
                  ))}
                  <Box sx={styles.addStatusButtonContainer}>
                    <Button sx={styles.button} onClick={handleAddStatus} disabled={addDisabled()}>
                      <AddIcon aria-hidden={true} />
                      {messages.addStatus}
                    </Button>
                  </Box>
                </Box>}
          </Box>

          <Box sx={styles.orientationSettingsContainer}>
            <Typography id="orientation" sx={styles.rowHeaderLabelPreview}>
              {messages.labelPreview}
            </Typography>
            {
              isLoading ? <Skeleton variant="rectangular" height={300} /> :

                <Box sx={styles.previewContainer}>
                  <RadioGroup
                    row
                    aria-labelledby="orientation"
                    name="orientation"
                    value={currentAlignment}
                    onChange={(e, value) => setCurrentAlignment(value as TimelineChartOrientation)}
                  >
                    <FormControlLabel value={TimelineChartOrientation.HORIZONTAL} control={<Radio />} label="Horizontal" />
                    <FormControlLabel value={TimelineChartOrientation.VERTICAL} control={<Radio />} label="Vertical" />
                  </RadioGroup>
                  <ProgressTrackerPreview
                    settings={LocalProgressTrackerSettings}
                    orientation={
                      currentAlignment === 'horizontal'
                        ? TimelineChartOrientation.HORIZONTAL
                        : TimelineChartOrientation.VERTICAL
                    }
                  />
                </Box>}
          </Box>
        </Box>
      </Box >
      <StatusColorModal
        messages={messages}
        open={showColorModal}
        onClose={() => setShowColorModal(false)}
        initialColor={LocalProgressTrackerSettings.find((setting) => setting.id == activeSettingId)?.colorCode || ''}
        onSave={handleColorSave}
      />
    </>
  );
};

export default ProgressTrackerSettings;
